import React, { useEffect, useMemo, useState } from 'react';

import EditorContext from 'context/EditorContext';
import { LocalDirtyProvider } from 'context/LocalDirtyContext';
import { UnderlineProvider } from 'context/UnderlineContext';
import VideoPlayerActionsContext from 'context/VideoPlayerActionsContext';
import VideoPlayerStateContext from 'context/VideoPlayerStateContext';
import FindReplaceContext from 'context/FindReplaceContext';
import CommandPaletteContext from 'context/CommandPaletteContext';
import PlaybackTimeContext from 'context/PlaybackTimeContext';
import PlaybackClockContext, {createPlaybackClock} from 'context/PlaybackClockContext';
import SpeakerFocusContext from 'context/SpeakerFocusContext';
import WordChunkEditorContext from 'context/WordChunkEditorContext';
import WordChunkSliderContext from 'context/WordChunkSliderContext';
import EditMenuContext from 'context/EditMenuContext';

const EditorContextsProvider = ({
  collectionId,
  isCorrector,
  isReviewer,
  pubkey,
  locale,
  children,
  openEditOptions,
  setOpenEditOptions,
  maxCPL,
  language,
  versionId,
  showScrollHelper
}) => {
  const splitLayoutEnabled = true;

  const [editorContextValues, setEditorContextValues] = useState({
    collectionId,
    isCorrector,
    isReviewer,
    locale,
    pubkey,
    openEditOptions,
    setOpenEditOptions,
    showScrollHelper,
    language,
    maxCPL,
    versionId,
    splitLayoutEnabled
  });

  useEffect(() => {
    setEditorContextValues({
      collectionId,
      versionId,
      language,
      isCorrector,
      isReviewer,
      pubkey,
      locale,
      openEditOptions,
      setOpenEditOptions,
      maxCPL,
      showScrollHelper,
      splitLayoutEnabled
    });
  }, [
    collectionId,
    versionId,
    isCorrector,
    language,
    locale,
    isReviewer,
    pubkey,
    openEditOptions,
    setOpenEditOptions,
    maxCPL,
    showScrollHelper,
    splitLayoutEnabled
  ]);

  const [videoPlayerActions, setVideoPlayerActions] = useState({
    setVideoPlayerActions: () => {},
    handleQuickRewind15: () => {},
    handleQuickRewind: () => {},
    handleFastForward: () => {},
    handleFastForward15: () => {},
    onPlay: () => {},
    onPause: () => {},
    goSlow: () => {},
    goNormal: () => {},
    goFast: () => {},
    goFaster: () => {},
    onSeek: () => {},
    scrollToSection: () => {}
  });
  useEffect(() => {
    setVideoPlayerActions((rest) => ({ ...rest, setVideoPlayerActions }));
  }, []);

  const [videoPlayerState, setVideoPlayerState] = useState({
    setVideoPlayerState: () => {},
    dockNode: null,
    playing: false,
    speed: 1,
    isInPiP: false,
    videoFrameHidden: false
  });

  const [findReplaceState, setFindReplaceState] = useState({
    setShow: () => {},
    show: false
  });

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const [playbackTime, setPlaybackTime] = useState({currentTime: 0, duration: 0});
  const [playbackClock] = useState(createPlaybackClock);

  useEffect(() => {
    playbackClock.publish(playbackTime.currentTime);
  }, [playbackClock, playbackTime.currentTime]);
  const [speakerFocus, setSpeakerFocus] = useState(() => new Set());
  const [rosterOpen, setRosterOpen] = useState(false);

  const [wordChunkEditorState, setWordChunkEditorState] = useState({
    setShow: () => {},
    showWarning: false,
    showEditor: false,
    spans: [],
    minTime: 0,
    maxTime: 0,
    useBlockProps: {}
  });

  const [wordChunkSliderState, setWordChunkSliderState] = useState({
    setShow: () => {},
    showSlider: window.localStorage.getItem('slider') === 'debug,open',
    spans: [],
    minTime: 0,
    maxTime: 0,
    useBlockProps: {}
  });

  const [editMenuState, setEditMenuState] = useState({
    setShow: () => {},
    showContext: false,
    showHighlights: false
  });

  useEffect(() => {
    setVideoPlayerState((rest) => ({ ...rest, setVideoPlayerState }));
    setFindReplaceState((rest) => ({ ...rest, setFindReplaceState }));
    setWordChunkEditorState((rest) => ({ ...rest, setWordChunkEditorState }));
    setWordChunkSliderState((rest) => ({ ...rest, setWordChunkSliderState }));
    setEditMenuState((rest) => ({ ...rest, setEditMenuState }));
  }, []);

  // Playback time updates every 200ms. Stable values keep those ticks from
  // re-rendering every consumer of the other contexts.
  const editorContextValue = useMemo(
    () => ({ ...editorContextValues, setEditorContextValues }),
    [editorContextValues]
  );
  const commandPaletteValue = useMemo(
    () => ({ open: commandPaletteOpen, setOpen: setCommandPaletteOpen }),
    [commandPaletteOpen]
  );
  const playbackTimeValue = useMemo(
    () => ({ ...playbackTime, setPlaybackTime }),
    [playbackTime]
  );
  const speakerFocusValue = useMemo(
    () => ({
      focus: speakerFocus,
      setFocus: setSpeakerFocus,
      rosterOpen,
      setRosterOpen
    }),
    [speakerFocus, rosterOpen]
  );
  const editMenuValue = useMemo(
    () => ({ ...editMenuState, setEditMenuState }),
    [editMenuState]
  );

  return (
    <EditorContext.Provider value={editorContextValue}>
      <VideoPlayerActionsContext.Provider value={videoPlayerActions}>
        <VideoPlayerStateContext.Provider value={videoPlayerState}>
          <FindReplaceContext.Provider value={findReplaceState}>
            <CommandPaletteContext.Provider value={commandPaletteValue}>
              <PlaybackTimeContext.Provider value={playbackTimeValue}>
                <PlaybackClockContext.Provider value={playbackClock}>
                <SpeakerFocusContext.Provider value={speakerFocusValue}>
                  <WordChunkEditorContext.Provider value={wordChunkEditorState}>
                    <WordChunkSliderContext.Provider value={wordChunkSliderState}>
                      <EditMenuContext.Provider value={editMenuValue}>
                        <UnderlineProvider>
                          <LocalDirtyProvider>{children}</LocalDirtyProvider>
                        </UnderlineProvider>
                      </EditMenuContext.Provider>
                    </WordChunkSliderContext.Provider>
                  </WordChunkEditorContext.Provider>
                </SpeakerFocusContext.Provider>
                </PlaybackClockContext.Provider>
              </PlaybackTimeContext.Provider>
            </CommandPaletteContext.Provider>
          </FindReplaceContext.Provider>
        </VideoPlayerStateContext.Provider>
      </VideoPlayerActionsContext.Provider>
    </EditorContext.Provider>
  );
};

export default EditorContextsProvider;
